import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingCart,
  FileDoc,
  FilePdf,
  CreditCard,
  Shield,
  Eye,
  CheckCircle,
  Buildings,
  EnvelopeSimple,
  DownloadSimple,
  Users,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { libraryImages } from '../assets/imageLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a backend `image_key` (e.g. "documentationCollage") to a webpack-
// bundled URL from the central Assets Library. Falls back to a neutral
// library image if the key is missing.
const resolveProductImage = (product) =>
  (product?.image_key && libraryImages[product.image_key]) ||
  libraryImages.documentationCollage;

const SocialProofBadge = ({ count }) => {
  // Gracefully handle the "early days" period with a different framing
  if (count === undefined) return null;
  const isEarly = count < 5;
  const label = isEarly
    ? 'كوني من أوائل المنشآت التي تعتمد الأداة'
    : `أكثر من ${count.toLocaleString('ar-SA')} منشأة اعتمدت الأداة`;
  const sublabel = isEarly
    ? 'عرض الإطلاق — انضمي إلى المنشآت الرائدة'
    : 'بناءً على مشتريات حقيقية من متجرنا';
  return (
    <div
      data-testid="social-proof-badge"
      className="flex items-center gap-3 p-3 mb-4 rounded-xl border border-[#D8D2C8] bg-gradient-to-l from-[#F5F2EC] to-white"
    >
      <div className="w-10 h-10 rounded-full bg-[#1F3D2B] flex items-center justify-center shrink-0">
        <Users size={20} weight="fill" className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-[#1F3D2B] leading-tight">{label}</p>
        <p className="text-xs text-[#7A866E] mt-0.5">{sublabel}</p>
      </div>
    </div>
  );
};

const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [statsByProduct, setStatsByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    organization_name: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then(r => r.json())
      .then(async d => {
        const list = d.products || [];
        setProducts(list);
        // Fetch purchase stats in parallel
        const entries = await Promise.all(
          list.map(p =>
            fetch(`${API_URL}/api/stats/product/${p.id}`)
              .then(r => r.json())
              .then(s => [p.id, s.organizations_count ?? 0])
              .catch(() => [p.id, 0])
          )
        );
        setStatsByProduct(Object.fromEntries(entries));
      })
      .catch(() => toast.error('حدث خطأ في تحميل المنتجات'))
      .finally(() => setLoading(false));
  }, []);

  const openPreview = (product) => {
    window.open(`${API_URL}/api/preview/${product.id}`, '_blank', 'noopener');
  };

  const openCheckout = (product) => {
    setSelected(product);
    setShowCheckout(true);
  };

  const submitCheckout = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.organization_name) {
      toast.error('يرجى إكمال جميع الحقول');
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: selected.id,
          customer_name: form.customer_name.trim(),
          customer_email: form.customer_email.trim(),
          organization_name: form.organization_name.trim(),
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 503) {
        // Payment gateway not yet configured (Moyasar pending).
        const msg =
          data?.detail?.message ||
          'بوابة الدفع قيد التهيئة. للاستفسار أو إتمام الشراء، يُرجى التواصل عبر واتساب.';
        toast.message('بوابة الدفع قيد التهيئة', {
          description: msg,
          duration: 6000,
        });
        setCheckoutLoading(false);
        return;
      }
      if (!res.ok) throw new Error(data?.detail || 'checkout failed');
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error('حدث خطأ في إنشاء جلسة الدفع، حاول مرة أخرى.');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="pt-24 min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full" />
      </main>
    );
  }

  return (
    <main data-testid="store-page" className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]" dir="rtl">
      <section className="py-12 bg-[#F5F2EC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-sm mb-4">
            المتجر الرقمي
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            أدوات رحلة الجودة
          </h1>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8">
            منتجات رقمية احترافية مع نسخة مخصّصة تحمل اسم منشأتك
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {products.length === 0 ? (
            <div className="text-center py-12 text-[#6B7565]">لا توجد منتجات متاحة حالياً</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {products.map((product, i) => (
                <motion.div
                  key={product.id}
                  data-testid={`store-product-${product.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={resolveProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                    <span className="absolute top-4 right-4 bg-[#4A641F] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      {product.stage_label}
                    </span>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#1F3D2B] mb-1 font-['IBM_Plex_Sans_Arabic'] leading-tight">
                      {product.name}
                    </h3>
                    <p className="text-xs text-[#92A082] mb-3">{product.name_en}</p>
                    <p className="text-[#6B7565] text-sm leading-7 mb-4 whitespace-pre-line">{product.description}</p>

                    {product.highlights?.length > 0 && (
                      <ul className="space-y-2 mb-4">
                        {product.highlights.map((h, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-[#3D512D]">
                            <CheckCircle size={16} weight="fill" className="text-[#4A641F] mt-0.5 shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="bg-[#F5F2EC] rounded-xl p-4 mb-4">
                      <p className="text-xs text-[#5D6E55] font-medium mb-3">الملفات المشمولة:</p>
                      <div className="space-y-2">
                        {product.files?.map((f, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-[#1F3D2B]">
                            {f.kind === 'pdf' ? (
                              <FilePdf size={18} className="text-red-500" />
                            ) : (
                              <FileDoc size={18} className="text-blue-500" />
                            )}
                            <span>{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <SocialProofBadge count={statsByProduct[product.id]} />

                    <div className="flex items-center justify-between pt-4 border-t border-[#E6E0D6] gap-3">
                      <div data-testid={`price-block-${product.id}`} className="flex flex-col">
                        {product.original_price && product.original_price > product.price && (
                          <div className="flex items-center gap-2 mb-0.5">
                            <span
                              data-testid={`original-price-${product.id}`}
                              className="text-sm sm:text-base text-red-600 line-through decoration-2 decoration-red-500"
                            >
                              {product.original_price?.toLocaleString('ar-SA')} ريال
                            </span>
                            <span
                              data-testid={`discount-badge-${product.id}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] sm:text-xs font-bold border border-red-200"
                            >
                              خصم {Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                            </span>
                          </div>
                        )}
                        <div className="flex items-baseline gap-1">
                          <span
                            data-testid={`current-price-${product.id}`}
                            className="text-3xl sm:text-4xl font-extrabold text-[#1F3D2B] leading-none"
                          >
                            {product.price?.toLocaleString('ar-SA')}
                          </span>
                          <span className="text-sm text-[#7A866E] font-medium">ريال</span>
                        </div>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-[11px] sm:text-xs text-[#4A641F] font-semibold mt-1">
                            وفّري {(product.original_price - product.price).toLocaleString('ar-SA')} ريال — عرض الإطلاق
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`preview-btn-${product.id}`}
                          onClick={() => openPreview(product)}
                          variant="outline"
                          className="border-[#D8D2C8] text-[#1F3D2B] rounded-xl flex items-center gap-2"
                        >
                          <Eye size={18} />
                          معاينة
                        </Button>
                        <Button
                          data-testid={`buy-btn-${product.id}`}
                          onClick={() => openCheckout(product)}
                          className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl flex items-center gap-2"
                        >
                          <ShoppingCart size={18} />
                          شراء الآن
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-[#F5F2EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { Icon: Shield, label: 'بوابة دفع آمنة (Moyasar — قريباً)' },
            { Icon: CreditCard, label: 'ريال سعودي مباشر' },
            { Icon: DownloadSimple, label: 'تحميل فوري بعد الدفع' },
            { Icon: EnvelopeSimple, label: 'رابط التحميل على البريد' },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-[#1F3D2B]/10 flex items-center justify-center mb-2">
                <Icon size={24} className="text-[#1F3D2B]" />
              </div>
              <span className="text-sm text-[#1F3D2B] font-medium">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {showCheckout && selected && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !checkoutLoading && setShowCheckout(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-[24px] max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <h3 className="text-xl font-bold text-[#1F3D2B] mb-1 font-['IBM_Plex_Sans_Arabic']">
              إتمام الشراء
            </h3>
            <p className="text-[#6B7565] text-sm mb-5">{selected.name}</p>

            <form onSubmit={submitCheckout}>
              <div className="space-y-4 mb-5">
                <div>
                  <Label htmlFor="customer_name">الاسم الكامل *</Label>
                  <Input
                    id="customer_name"
                    data-testid="checkout-name-input"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    required
                    className="mt-1 rounded-xl"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
                <div>
                  <Label htmlFor="organization_name" className="flex items-center gap-1.5">
                    <Buildings size={16} className="text-[#4A641F]" />
                    اسم المنشأة *
                  </Label>
                  <Input
                    id="organization_name"
                    data-testid="checkout-org-input"
                    value={form.organization_name}
                    onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                    required
                    className="mt-1 rounded-xl"
                    placeholder="مثال: روضة الأمل التعليمية"
                  />
                  <p className="text-xs text-[#7A866E] mt-1.5">
                    سيظهر هذا الاسم على رأس وتوقيع الأداة (Header + Watermark)
                  </p>
                </div>
                <div>
                  <Label htmlFor="customer_email">البريد الإلكتروني *</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    data-testid="checkout-email-input"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    required
                    className="mt-1 rounded-xl"
                    placeholder="example@email.com"
                    dir="ltr"
                  />
                  <p className="text-xs text-[#7A866E] mt-1.5">سيصلك رابط التحميل على هذا البريد</p>
                </div>
              </div>

              <div className="bg-[#F5F2EC] rounded-xl p-4 mb-5 flex justify-between items-center">
                <span className="text-[#6B7565]">المجموع</span>
                <span className="text-2xl font-bold text-[#1F3D2B]">
                  {selected.price?.toLocaleString('ar-SA')} ريال
                </span>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setShowCheckout(false)}
                  disabled={checkoutLoading}
                  variant="outline"
                  className="flex-1 border-[#D8D2C8] text-[#1F3D2B] rounded-xl py-3"
                >
                  إلغاء
                </Button>
                <Button
                  type="submit"
                  data-testid="proceed-to-payment-btn"
                  disabled={checkoutLoading}
                  className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-3 flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      جاري التحويل...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} />
                      الدفع الآمن
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-[#92A082] text-center mt-4">
                بوابة الدفع (Moyasar) قيد التهيئة — سيتم تفعيل الدفع قريباً.
              </p>
              <div className="mt-4 p-3 bg-[#F5F2EC] rounded-xl border border-[#E6E0D6]">
                <p className="text-[11px] text-[#3D512D] leading-6">
                  <strong className="text-[#1F3D2B]">ترخيص الاستخدام:</strong>{' '}
                  بإتمام الشراء، يحصل العميل (شخصياً أو نيابةً عن جهته) على{' '}
                  <strong>حق استخدام شخصي/مؤسسي محدود</strong> للمحتوى الرقمي
                  فقط، ولا يتم نقل ملكية حقوق المؤلف أو الملكية الفكرية. يُمنع
                  إعادة البيع أو إعادة التوزيع أو إعادة النشر بأي وسيلة دون إذن
                  خطي مسبق.{' '}
                  <a
                    href="/legal/intellectual-property"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4A641F] hover:underline"
                  >
                    سياسة الملكية الفكرية
                  </a>
                  .
                </p>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </main>
  );
};

export default StorePage;
